import React from 'react';

/**
 * ContentRenderer - Renders HTML content safely with proper styling
 * This component ensures WYSIWYG consistency between editor and viewer
 * 
 * Features:
 * - Renders HTML from TipTap editor exactly as authored
 * - Falls back to Markdown parsing for legacy content
 * - Proper table rendering with responsive design
 * - Text wrapping and overflow handling
 * - Consistent typography
 */

// Legacy markdown parser for backward compatibility
function parseMarkdownToHtml(text) {
  if (!text) return '';
  
  // If content already has HTML tags, assume it's from the rich text editor
  if (text.includes('<p>') || text.includes('<h1>') || text.includes('<table>') || text.includes('<ul>') || text.includes('<ol>')) {
    return text;
  }
  
  let html = text;

  // First, normalize line breaks - replace single newlines with spaces (except in code blocks and tables)
  // This prevents mid-word breaks when content has hard line breaks
  
  // Tables (Markdown format) - process first before normalizing newlines
  const tableRegex = /\n?\|(.+)\|\n\|[-| :]+\|\n((?:\|.+\|\n?)+)/g;
  html = html.replace(tableRegex, (match, header, body) => {
    const headers = header.split('|').map((h) => h.trim()).filter(Boolean);
    const rows = body.trim().split('\n').map((row) =>
      row.split('|').map((c) => c.trim()).filter(Boolean)
    );
    return `\n\n<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table>\n\n`;
  });

  // Code blocks - preserve them
  const codeBlocks = [];
  html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
    codeBlocks.push(`<pre><code>${code}</code></pre>`);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  // Now normalize single newlines to spaces (double newlines become paragraph breaks)
  // This fixes the mid-word breaking issue
  html = html.replace(/([^\n])\n([^\n])/g, '$1 $2');
  
  // Restore code blocks
  codeBlocks.forEach((block, i) => {
    html = html.replace(`__CODE_BLOCK_${i}__`, block);
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  // Numbered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  // Bullet lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  // Wrap consecutive li in ul
  html = html.replace(/((?:<li>.*<\/li>\s*)+)/g, '<ul>$1</ul>');
  // Paragraphs (double newlines)
  html = html.replace(/\n\n+/g, '</p><p>');
  html = `<p>${html}</p>`;
  // Clean empty p
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<p>\s*(<h[123]>)/g, '$1');
  html = html.replace(/(<\/h[123]>)\s*<\/p>/g, '$1');
  html = html.replace(/<p>\s*(<table>)/g, '$1');
  html = html.replace(/(<\/table>)\s*<\/p>/g, '$1');
  html = html.replace(/<p>\s*(<ul>)/g, '$1');
  html = html.replace(/(<\/ul>)\s*<\/p>/g, '$1');
  html = html.replace(/<p>\s*(<pre>)/g, '$1');
  html = html.replace(/(<\/pre>)\s*<\/p>/g, '$1');

  return html;
}

export default function ContentRenderer({ content, className = '' }) {
  if (!content) {
    return (
      <div className={`prose-content ${className}`}>
        <p className="text-slate-400 italic">No content available.</p>
      </div>
    );
  }

  const htmlContent = parseMarkdownToHtml(content);

  return (
    <div
      className={`prose-content content-renderer ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}
